import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, ArrowRight, Loader2, Wand2, FlaskConical } from "lucide-react";

// Illustrated empty-state for the Dashboard. Two paths:
//   1) "Load sample pipeline" — calls POST /api/demo/seed → instant 3 firms + 3 synergies
//   2) "Start fresh" — link to /app/research
// Shown only when the workspace is empty (total_firms === 0).
export default function EmptyDashboard({ onSeeded }) {
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const seed = async () => {
        setLoading(true); setErr("");
        try {
            await api.post("/demo/seed");
            // Notify parent to refetch /stats; small delay so Mongo writes settle.
            setTimeout(() => { onSeeded && onSeeded(); }, 250);
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "string" ? d : "Failed to seed demo data.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="border border-zinc-800 bg-zinc-950 p-10 sm:p-14 relative overflow-hidden" data-testid="empty-dashboard">
            {/* decorative grid */}
            <div
                className="absolute inset-0 opacity-[0.04] pointer-events-none"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(245,158,11,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.6) 1px, transparent 1px)",
                    backgroundSize: "40px 40px",
                }}
            />
            <div className="relative max-w-3xl">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">// Workspace · empty</div>
                <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-zinc-50">
                    Your terminal is quiet.<br />Let's wake it up.
                </h2>
                <p className="text-zinc-400 text-base leading-relaxed mt-4 max-w-xl">
                    Affynix lights up once you have a few firms in your library and a synergy or two pinned. Pick how you want to start.
                </p>

                {/* SVG art — three glowing "node" cards floating */}
                <div className="mt-8 grid grid-cols-3 gap-3 max-w-md" aria-hidden="true">
                    {[
                        { c: "#F59E0B", h: "h-20" },
                        { c: "#10B981", h: "h-28" },
                        { c: "#3B82F6", h: "h-16" },
                    ].map((b, i) => (
                        <div key={i} className="relative">
                            <div className={`${b.h} border border-zinc-800 bg-black/60 flex flex-col justify-end p-2`}>
                                <div className="w-full h-px bg-zinc-800 mb-2" />
                                <div className="w-2 h-2 mb-1" style={{ background: b.c }} />
                                <div className="w-6 h-1 bg-zinc-800" />
                                <div className="w-10 h-1 bg-zinc-800 mt-1" />
                            </div>
                            <div className="absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full" style={{ background: b.c, boxShadow: `0 0 12px ${b.c}` }} />
                        </div>
                    ))}
                </div>

                {/* CTAs */}
                <div className="mt-8 flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        onClick={seed}
                        disabled={loading}
                        data-testid="seed-demo-btn"
                        className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-black font-bold text-sm uppercase tracking-wider px-5 py-3 transition-all hover:-translate-y-0.5"
                    >
                        {loading
                            ? <><Loader2 className="w-4 h-4 animate-spin" /> Seeding…</>
                            : <><FlaskConical className="w-4 h-4" /> Load sample pipeline</>}
                    </button>
                    <Link
                        to="/app/research"
                        data-testid="empty-start-research"
                        className="inline-flex items-center gap-2 border border-zinc-800 hover:border-amber-500 text-zinc-300 hover:text-amber-400 font-mono text-xs uppercase tracking-wider px-5 py-3 transition-colors"
                    >
                        <Wand2 className="w-3.5 h-3.5" /> Start fresh — research a firm <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
                {err && (
                    <div className="mt-4 text-xs font-mono text-red-400 border border-red-900/60 bg-red-950/30 px-3 py-2 inline-block" data-testid="seed-error">
                        {err}
                    </div>
                )}

                <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                    <Sparkles className="inline w-3 h-3 mr-1 text-amber-500/70" />
                    Sample seeds 3 firms (Stripe · Shopify · Notion) + 3 high-fit synergies — instantly removable later.
                </div>
            </div>
        </div>
    );
}

// Lightweight illustrated empty-state for ANY list view (Library, Board, Inbox, etc.).
// Designed to be drop-in: pass a title + body + cta link.
export function EmptyState({ icon: Icon, title, body, cta, ctaHref, testid }) {
    return (
        <div className="border border-dashed border-zinc-800 p-10 text-center" data-testid={testid || "empty-state"}>
            {Icon && (
                <div className="w-12 h-12 mx-auto border border-zinc-800 bg-zinc-950 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-amber-500/70" />
                </div>
            )}
            <div className="font-heading font-bold text-zinc-100 text-base mb-1">{title}</div>
            <div className="text-sm text-zinc-500 leading-relaxed max-w-md mx-auto">{body}</div>
            {cta && ctaHref && (
                <Link to={ctaHref} className="inline-flex items-center gap-1.5 mt-4 font-mono text-xs uppercase tracking-wider text-amber-500 hover:text-amber-400 border-b border-amber-500/40 hover:border-amber-400 pb-0.5 transition-colors">
                    {cta} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
            )}
        </div>
    );
}
